import { ApiService } from '../api/api.service';
import { camelCaseObject, getCountryCodeByTopLevelDomain, getFeatureFlagDefaults } from '@/helpers';
import { GetOrderingStatusFeatureFlagsResponse } from '@/models/feature-flags';
import { Config } from '@/environment';

import { MUTATIONS as FEATURE_FLAGS_MUTATIONS } from '@/store/feature-flags';
import router from '@/router';
import { Store } from 'vuex';
import { AppState } from '@/store';
import { Logger } from '../logger';
import { AnalyticsService } from '../analytics/analytics.service';
import { AxiosInstance } from 'axios';
import { SplitFactory } from '@splitsoftware/splitio';

export interface FeatureFlagsService {
  ensureApimFeatureFlagsFetched(): Promise<void>;
  refreshApimFeatureFlags(hostName?: string): Promise<void>;
  validateOnlineOrdering(): Promise<void>;
  waitForSplitReady(): Promise<void>;
}

export class AppFeatureFlagsService extends ApiService implements FeatureFlagsService {
  private splitSDK?: SplitIO.IBrowserSDK;
  private customerFlags?: SplitIO.IBrowserClient;

  private static DEFAULT_ATTRIBUTES = {
    workloadType: 'client',
    workload: 'client-order-web',
  };

  private static DEFAULT_CUSTOMER_ID_HASH = '-';

  constructor(
    private readonly config: Config,
    protected store: Store<AppState>,
    protected httpClient: AxiosInstance,
    protected logger: Logger,
    protected analytics: AnalyticsService,
    private splitFactory: typeof SplitFactory,
  ) {
    super(store, httpClient, logger, analytics);

    // create a watcher that tracks changes to the user customerIdHash to update the Split client
    this.store.watch(
      (state) => state.customer.profile?.customerIdHash,
      (customerIdHash) => {
        this.updateClient(customerIdHash || AppFeatureFlagsService.DEFAULT_CUSTOMER_ID_HASH);
      },
      { immediate: true },
    );

    // When a guest selects a restaurant, re-initialise the Split client keyed on the restaurant number
    this.store.watch(
      (state) => state.restaurant.selectedRestaurant?.restaurantNumber,
      (restaurantNumber) => {
        const isGuest = this.store.state.customer.profile?.isGuest;
        if (!isGuest) return;

        const key = restaurantNumber?.toString()
          ?? AppFeatureFlagsService.DEFAULT_CUSTOMER_ID_HASH;

        this.updateClient(key);
      },
    );
  }

  private updateClient(customerIdHash: string): void {
    if (!!this.splitFactory) {
      // Remove existing listeners and destroy existing SDK instance without waiting for Promise to complete.
      this.customerFlags?.removeAllListeners();
      const _ = this.splitSDK?.destroy();
    }

    this.splitSDK = this.splitFactory({
      core: {
        authorizationKey: this.config.split.authorizationKey,
        key: AppFeatureFlagsService.DEFAULT_CUSTOMER_ID_HASH,
      },
      startup: {
        readyTimeout: 2000, // 2 seconds
      },
    });
   
    
    this.customerFlags = this.splitSDK.client(this.resolveSplitKey(customerIdHash));

    this.customerFlags.setAttributes(AppFeatureFlagsService.DEFAULT_ATTRIBUTES);

    // event handlers to update flags in the store when the SDK is ready or when there is an update
    this.customerFlags.on(this.customerFlags.Event.SDK_READY, () => {
      this.onUpdate(customerIdHash);
    });

    this.customerFlags.on(this.customerFlags.Event.SDK_UPDATE, () => {
      this.onUpdate(customerIdHash);
    });

    this.customerFlags.on(this.customerFlags.Event.SDK_READY_TIMED_OUT, () => {
      this.onUpdate(customerIdHash); // proceed with default/empty flags
    });
  }

  // If customer is a guest, key on restaurant number (or the anonymous sentinel when none is selected).
  // Non-guests are always keyed on their customerIdHash.
  private resolveSplitKey(customerIdHash: string): string {
    const restaurantNumber = this.store.state.restaurant.selectedRestaurant?.restaurantNumber;
    return this.store.state.customer.profile?.isGuest
      ? (restaurantNumber?.toString() ?? AppFeatureFlagsService.DEFAULT_CUSTOMER_ID_HASH)
      : customerIdHash;
  }

  private onUpdate(customerIdHash?: string): void {
    if(this.store.state.customer.jwt && (customerIdHash != this.store.state.customer.profile?.customerIdHash)) {
      return;
    }
    // Update store with timestamp showing most recent update, including customer hash
    const timeStampedCustomerHash = `${customerIdHash}:${Date.now()}`;

    this.store.commit(FEATURE_FLAGS_MUTATIONS.SET_CUSTOMER_FLAGS_KEY, timeStampedCustomerHash);

    this.setFlags();
  }

  public setFlags(): void {
    // update the store with the feature flags in use
    const getFlagValue = (flagName: string): boolean => {
      return this.customerFlags?.getTreatment(flagName) === 'on';
    };
    
    this.store.commit(FEATURE_FLAGS_MUTATIONS.SET_FEATURE_FLAGS, {
      isSummerOfExtrasEnabled: getFlagValue('configure-is-summer-of-extras-enabled'),
      isUpdatedContextualRewardsBagUpdatesEnabled: getFlagValue('is-updated-contextual-rewards-bag-updates-enabled'),
      isChooseYourOwnBirthdayEnabled: getFlagValue('release-choose-your-own-birthday'),
      isGoogleMapsApiKeyEnabled: getFlagValue('release-googlemaps-apikey'),
      isRewardsReferralProgramEnabled: getFlagValue('release-rewards-referral-program'),
      isCyoRewardAndSideQuestsEnabled: getFlagValue('release-cyo-reward-and-side-quests'),
      isGooglePayWebReleaseEnabled: getFlagValue('release-google-pay-web'),
      isChipotleUReverificationEnabled: getFlagValue('release-chipotle-u-reverification'),
      isSummerAmoeWeeklyEnabled: getFlagValue('configure-is-summer-amoe-weekly-enabled'),
      isSummerAmoeGrandPrizeEnabled: getFlagValue('configure-is-summer-amoe-grand-prize-enabled'),
    });
  }

  waitForSplitReady(): Promise<void> {
    return new Promise((resolve) => {
      if (this.store.state.featureFlags.customerFlagsKey) {
        resolve();
        return;
      }

      const unwatch = this.store.watch(
        (state) => state.featureFlags.customerFlagsKey,
        (newValue) => {
          if (newValue) {
            unwatch();
            resolve();
          }
        }
      );
    });
  }

  async ensureApimFeatureFlagsFetched() {
    if (this.store.state.featureFlags.hasFetchedFlags === false) {
      await this.refreshApimFeatureFlags();
    }
  }

  async validateOnlineOrdering() {
    await this.refreshApimFeatureFlags();
    const flags = this.store.state.featureFlags;
    if (flags.orderingStatus && (!flags.orderingStatus.isWebOrderingOnline || !flags.orderingStatus.isOnline)) {
      router.replace({ path: '/offline' });
    }
  }

  async refreshApimFeatureFlags(hostName: string = window.location.hostname): Promise<void> {
    const country = getCountryCodeByTopLevelDomain(hostName);
    const featureFlagDefaults = getFeatureFlagDefaults(country);

    return this.httpClient
      .get('/onlineorderingstatus', { params: { country } })
      .then((r) => {
        const serviceFlags = camelCaseObject<GetOrderingStatusFeatureFlagsResponse>(r.data);
        const mergedFlags = { ...featureFlagDefaults, ...serviceFlags };
        this.store.commit(FEATURE_FLAGS_MUTATIONS.SET_ORDERING_STATUS_FLAGS, mergedFlags);
      })
      .finally(() => this.store.commit(FEATURE_FLAGS_MUTATIONS.SET_HAS_FETCHED_FLAGS, true));
  }
}